import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { UserRoundCheck, Users, Wrench, IdCard, PackageOpen, Settings, ChevronRight } from 'lucide-react';
import { getEnabledServices } from '../../utils/sessionDB';
import { isServiceEnabled, openServiceApp } from '../../utils/mobileServices';
import './MobileBottomNavigation.css';

const destinations = [
  { label: 'HRMS ESS', service: 'HRMS ESS', to: '/dashboards/ess', icon: UserRoundCheck },
  { label: 'HRMS Team', service: 'HRMS Team', to: '/dashboards/hr', icon: Users },
  { label: 'FSM', service: 'FSM', to: '/dashboards/fsm', icon: Wrench },
  { label: 'CRM', service: 'CRM', to: '/dashboards/crm', icon: IdCard },
  { label: 'IMS', service: 'IMS', to: '/dashboards/ims', icon: PackageOpen },
  { label: 'PMS', service: 'PSM', to: '/dashboards/pms', icon: Settings },
];

export function MobileBottomNavigation() {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const moreRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [enabledServices, setEnabledServices] = useState(getEnabledServices);
  useEffect(() => {
    const refresh = () => setEnabledServices(getEnabledServices());
    window.addEventListener('session-updated', refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener('session-updated', refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);
  const visibleDestinations = destinations.filter(({ service }) =>
    isServiceEnabled(service, enabledServices));
  const primaryDestinations = visibleDestinations.slice(0, 4);
  const overflowDestinations = visibleDestinations.slice(4);

  useEffect(() => setOpen(false), [location]);
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
        moreRef.current?.focus();
      }
    };
    const media = window.matchMedia('(min-width: 768px)');
    const onResize = () => { if (media.matches) setOpen(false); };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    media.addEventListener('change', onResize);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      media.removeEventListener('change', onResize);
    };
  }, [open]);

  const renderLink = ({ label, service, to, icon: Icon }) => (
    <NavLink key={label} to={to} end className={({ isActive }) =>
      `mobile-bottom-nav__item${isActive ? ' mobile-bottom-nav__item--active' : ''}`}
      onClick={async event => {
        if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        setOpen(false);
        if (!isServiceEnabled(service)) return;
        if (!(await openServiceApp(service)) && isServiceEnabled(service)) navigate(to);
      }}>
      <Icon size={22} aria-hidden="true" />
      <span>{label}</span>
    </NavLink>
  );

  return (
    <nav className="mobile-bottom-nav" aria-label="Dashboard navigation" ref={containerRef}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}>
      {primaryDestinations.map(renderLink)}
      {overflowDestinations.length > 0 && (<div className="mobile-bottom-nav__more">
        <button type="button" ref={moreRef}
          className={`mobile-bottom-nav__item${open || overflowDestinations.some(item => item.to === location.pathname) ? ' mobile-bottom-nav__item--active' : ''}`}
          aria-label="More dashboards" aria-expanded={open} aria-controls="mobile-more-dashboards"
          onClick={() => setOpen(value => !value)}>
          <ChevronRight size={22} aria-hidden="true" />
          <span>More</span>
        </button>
        {open && (
          <div id="mobile-more-dashboards" className="mobile-bottom-nav__options">
            {overflowDestinations.map(renderLink)}
          </div>
        )}
      </div>)}
    </nav>
  );
}